import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';
import {
  TIMER_TICK_MS,
  computeTimerPercent,
  resolveTimerLabel,
  type WorldSlug,
} from '@/lib/story-output/progress-timer';

type StreamStatus = 'connecting' | 'active' | 'completed' | 'failed';

interface UseStoryJobStreamOptions {
  world: WorldSlug;
}

interface UseStoryJobStreamResult {
  displayPercent: number;
  currentLabel: string;
  status: StreamStatus;
  storyId: string | null;
  error: string | null;
  errorCode: string | null;
  errorCta: { label: string; target: string } | null;
  /** Workstream F.6 — backend `user_message` from the failure SSE event,
   *  exposed separately from `error` so consumers can feed
   *  `<StoryErrorBlock errorEnvelope />` for the hybrid pattern. Null when
   *  the stream never delivered a failure event with a user_message. */
  userMessage: string | null;
}

// Wire contract from src/story-jobs/story-jobs.controller.ts. After Batch
// 6.1.1 the hook only consumes storyId + terminal status signals — per-step
// progress/percent fields are still forwarded but intentionally unused.
interface SseEvent {
  jobId?: string;
  status?: 'queued' | 'processing' | 'completed' | 'failed';
  stepStatus?: 'snapshot' | 'running' | 'completed' | 'skipped' | 'failed';
  progressPercent?: number;
  storyId?: string | null;
  errorStep?: string | null;
  errorMessage?: string | null;
  error_code?: string;
  error_stage?: string;
  user_message?: string;
  ui_action?: string;
  cta?: { label: string; target: string };
  type?: 'heartbeat' | 'init';
  timestamp?: string;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * useStoryJobStream — story-generation loading-screen state.
 *
 * Progress bar and label are driven by a local easing timer (see
 * progress-timer.ts), NOT by SSE event timing. This decouples UX from
 * backend step durations and from dev-tunnel SSE batching. The SSE
 * subscription only watches for two signals: terminal completion
 * (→ navigate) and terminal failure (→ error screen). storyId is
 * captured as early as possible so navigation on completion is lossless.
 */
export function useStoryJobStream(
  jobId: string,
  opts: UseStoryJobStreamOptions,
): UseStoryJobStreamResult {
  const { world } = opts;
  const accessToken = useAuthStore((s) => s.accessToken);

  const [displayPercent, setDisplayPercent] = useState(0);
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [storyId, setStoryId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorCta, setErrorCta] = useState<{ label: string; target: string } | null>(null);
  const [userMessage, setUserMessage] = useState<string | null>(null);

  // ── Local easing timer ────────────────────────────────────────
  // Runs while the job is in-flight. Ticks every 100ms, computes an eased
  // percent from elapsed time, caps at 95%. Cleanup on unmount and on
  // terminal state (completed/failed) prevents any stray tick from
  // overwriting the 100%-on-completion value set by the SSE handler.
  useEffect(() => {
    if (status === 'completed' || status === 'failed') return;

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setDisplayPercent(computeTimerPercent(elapsed, world));
    }, TIMER_TICK_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [jobId, world, status]);

  // ── SSE subscription ──────────────────────────────────────────
  useEffect(() => {
    if (!accessToken) return;

    // Native EventSource (not fetch-event-source) so iOS Safari streams
    // chunks live instead of buffering them in fetch()'s ReadableStream.
    // Access token rides in the query string — backend carve-out:
    // JwtSseAuthGuard on the stream endpoint only, path-checked at runtime.
    const url = `${API_BASE}/api/stories/jobs/${jobId}/stream?access_token=${encodeURIComponent(
      accessToken,
    )}`;
    const source = new EventSource(url);

    source.onmessage = (ev) => {
      if (!ev.data) return;
      let parsed: SseEvent;
      try {
        parsed = JSON.parse(ev.data) as SseEvent;
      } catch {
        return;
      }

      // Heartbeat / init nudge — no state effect. The init event is sent
      // at subscribe-time to flush proxy buffers; heartbeats fire every 15s.
      if (parsed.type === 'heartbeat' || parsed.type === 'init') return;

      // Failure branch
      if (parsed.stepStatus === 'failed' || parsed.status === 'failed') {
        // Bug #3 Part 1 (2026-05-12): surface error_stage to DevTools
        // console so Sebastian-side browser forensics can identify which
        // pipeline step failed without backend log access. error_code +
        // error_stage are dev-diagnostic only; user-facing copy lives in
        // user_message. PWA ESLint allows console.warn/error but not
        // console.log; use console.warn for parity with the existing
        // PWA logging convention.
        console.warn(
          '[Pipeline failure]',
          'error_stage:',
          parsed.error_stage,
          'error_code:',
          parsed.error_code,
          'errorStep:',
          parsed.errorStep,
        );
        setStatus('failed');
        setError(
          parsed.user_message ??
            parsed.errorMessage ??
            STRINGS.storyOutput.loading.errors.generationFailed,
        );
        setErrorCode(parsed.error_code ?? null);
        setErrorCta(parsed.cta ?? null);
        setUserMessage(parsed.user_message ?? null);
        if (parsed.storyId) setStoryId(parsed.storyId);
        source.close();
        return;
      }

      setStatus((prev) => (prev === 'connecting' ? 'active' : prev));

      // Capture storyId as early as possible so terminal completion can
      // navigate without an extra fetch. parsed.step / parsed.progressPercent
      // are intentionally ignored — the local timer drives percent and label.
      if (parsed.storyId) {
        setStoryId((prev) => prev ?? parsed.storyId ?? null);
      }

      // Terminal success — snap to 100 and close the stream. The timer
      // effect's cleanup runs on the status change and clears the interval,
      // so no tick can overwrite 100. OutputRoute swaps the CSS transition
      // duration to 300ms on completion for a visible polish beat.
      if (parsed.status === 'completed' || parsed.progressPercent === 100) {
        setStatus('completed');
        setDisplayPercent(100);
        source.close();
      }
    };

    source.onerror = () => {
      setStatus('failed');
      setError(STRINGS.storyOutput.loading.errors.connectionLost);
      source.close();
    };

    return () => {
      source.close();
    };
  }, [jobId, accessToken]);

  const currentLabel = resolveTimerLabel(displayPercent, world);

  return {
    displayPercent,
    currentLabel,
    status,
    storyId,
    error,
    errorCode,
    errorCta,
    userMessage,
  };
}
